import { useApiQuery, useApiMutation } from "./use-api";
import type { ApiSuccessResponse } from "../api-client";
import { STALE_TIME } from "../query-stale-times";

// ============================================================================
// Types
// ============================================================================

export interface Company {
  id: string;
  name: string;
  abbreviation: string;
  taxId: string | null;
  branchNo: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  defaultCurrency: string;
  fiscalYearStart: string;
  // Default Accounts
  defaultArAccountId: string | null;
  defaultApAccountId: string | null;
  defaultCashAccountId: string | null;
  defaultWhtReceivableAccountId: string | null;
  defaultWhtPayableAccountId: string | null;
  undueOutputVatAccountId: string | null;
  undueInputVatAccountId: string | null;
  /** 1 = เลือกภาษี/หัก ณ ที่จ่าย ต่อรายการ, 0 = รวมทั้งบิล */
  defaultLineLevelTax: number;
  createdAt: string;
}

export interface Branch {
  id: string;
  companyId: string;
  name: string;
  branchNo: string | null;
  address: string | null;
  phone: string | null;
  isActive: number;
  createdAt?: string;
}

export interface CompanyModule {
  id?: string;
  module: string;
  isEnabled: number;
  isSystem: boolean;
  lifecycleState?: "available" | "installed" | "enabled" | "disabled" | "upgrading" | "failed";
  installedVersion?: string | null;
  codeVersion?: string | null;
  dependencies?: string[];
  requiredPlatformCapabilities?: string[];
  missingPlatformCapabilities?: string[];
  needsUpgrade?: boolean;
  enabledAt: string | null;
  enabledBy: string | null;
}

export interface PlatformModule {
  id: string;
  name: string;
  nameTh: string | null;
  description: string | null;
  icon: string;
  color: string;
  isAlwaysEnabled: boolean;
  isAvailable: boolean;
  sortOrder: number;
  routePath: string | null;
  category: 'core' | 'finance' | 'operations' | 'crm' | 'hr' | 'logistics' | 'custom';
  isSystemModule: boolean;
  dependencies: string[];
  doctypes: Array<{ key: string; label: string }>;
  availableHooks: Array<{ event: string; label: string }>;
  customForms: Array<{ key: string; label: string }>;
}

export interface CreateBranchInput {
  companyId: string;
  name: string;
  branchNo?: string | null;
  address?: string;
  phone?: string;
}

export interface UpdateBranchInput {
  name?: string;
  branchNo?: string | null;
  address?: string | null;
  phone?: string | null;
}

export interface UpdateCompanyInput {
  name?: string;
  abbreviation?: string;
  taxId?: string | null;
  branchNo?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  defaultCurrency?: string;
  fiscalYearStart?: string;
  // Default Accounts
  defaultArAccountId?: string | null;
  defaultApAccountId?: string | null;
  defaultCashAccountId?: string | null;
  defaultWhtReceivableAccountId?: string | null;
  defaultWhtPayableAccountId?: string | null;
  undueOutputVatAccountId?: string | null;
  undueInputVatAccountId?: string | null;
  defaultLineLevelTax?: number;
}

// ============================================================================
// Companies
// ============================================================================

export function useCompanies() {
  return useApiQuery<ApiSuccessResponse<Company[]>>("/core/companies");
}

export function useCompany(companyId: string | null) {
  return useApiQuery<ApiSuccessResponse<Company>>(
    `/core/companies/${companyId}`,
    { enabled: !!companyId },
  );
}

export function useUpdateCompany(companyId: string | null) {
  return useApiMutation<ApiSuccessResponse<Company>, UpdateCompanyInput>(
    "patch",
    `/core/companies/${companyId}`,
  );
}

// ============================================================================
// Branches
// ============================================================================

export function useBranches(companyId: string | null) {
  return useApiQuery<ApiSuccessResponse<Branch[]>>("/core/branches", {
    params: companyId ? { companyId } : undefined,
    enabled: !!companyId,
  });
}

export function useCreateBranch() {
  return useApiMutation<ApiSuccessResponse<Branch>, CreateBranchInput>(
    "post",
    "/core/branches",
  );
}

export function useUpdateBranch(branchId: string | null) {
  return useApiMutation<ApiSuccessResponse<Branch>, UpdateBranchInput>(
    "patch",
    `/core/branches/${branchId}`,
  );
}

export function useToggleBranchActive(branchId: string | null) {
  return useApiMutation<ApiSuccessResponse<Branch>, void>(
    "patch",
    `/core/branches/${branchId}/toggle`,
  );
}

export function useDeleteBranch(branchId: string | null) {
  return useApiMutation<ApiSuccessResponse<{ message: string }>, void>(
    "delete",
    `/core/branches/${branchId}`,
  );
}

// ============================================================================
// Contact Channels
// ============================================================================

export interface ContactChannel {
  id: string;
  companyId: string;
  name: string;
  icon: string | null;
  color: string | null;
  isActive: number;
  sortOrder: number;
  createdAt: string;
  deletedAt: string | null;
}

export interface CreateContactChannelInput {
  companyId: string;
  name: string;
  icon?: string | null;
  color?: string | null;
  isActive?: number;
  sortOrder?: number;
}

export interface UpdateContactChannelInput {
  name?: string;
  icon?: string | null;
  color?: string | null;
  isActive?: number;
  sortOrder?: number;
}

export function useContactChannels(companyId: string | null) {
  return useApiQuery<ApiSuccessResponse<ContactChannel[]>>(
    "/core/contact-channels",
    {
      params: companyId ? { companyId } : undefined,
      enabled: !!companyId,
    },
  );
}

export function useActiveContactChannels(companyId: string | null) {
  return useApiQuery<ApiSuccessResponse<ContactChannel[]>>(
    "/core/contact-channels",
    {
      params: companyId ? { companyId, isActive: 1 } : undefined,
      enabled: !!companyId,
    },
  );
}

export function useCreateContactChannel() {
  return useApiMutation<ApiSuccessResponse<ContactChannel>, CreateContactChannelInput>(
    "post",
    "/core/contact-channels",
  );
}

export function useUpdateContactChannel(channelId: string | null) {
  return useApiMutation<ApiSuccessResponse<ContactChannel>, UpdateContactChannelInput>(
    "patch",
    `/core/contact-channels/${channelId}`,
  );
}

export function useDeleteContactChannel(channelId: string | null) {
  return useApiMutation<ApiSuccessResponse<{ message: string }>, void>(
    "delete",
    `/core/contact-channels/${channelId}`,
  );
}

// ============================================================================
// Company Modules
// ============================================================================

export function useCompanyModules(companyId: string | null) {
  return useApiQuery<ApiSuccessResponse<CompanyModule[]>>(
    "/core/company-modules",
    {
      params: companyId ? { companyId } : undefined,
      enabled: !!companyId,
    },
  );
}

export function usePlatformModules() {
  return useApiQuery<ApiSuccessResponse<PlatformModule[]>>(
    "/platform/modules",
    { staleTime: STALE_TIME.SETTINGS, refetchOnWindowFocus: false }, // settings — rarely changes
  );
}

export function useEnableModule() {
  return useApiMutation<
    ApiSuccessResponse<CompanyModule>,
    { companyId: string; module: string }
  >("post", "/core/company-modules", {
    // Cross-resource: enabling a module also updates the tenant's enabled
    // modules list served from /platform/modules/my-tenant.
    invalidateKeys: [["platform", "modules", "my-tenant"]],
  });
}

export function useDisableModule(moduleName: string, companyId: string | null) {
  return useApiMutation<ApiSuccessResponse<{ message: string }>, void>(
    "delete",
    `/core/company-modules/${moduleName}?companyId=${companyId}`,
    {
      // Cross-resource: disabling a module also updates the tenant's enabled
      // modules list served from /platform/modules/my-tenant.
      invalidateKeys: [["platform", "modules", "my-tenant"]],
    },
  );
}

// ============================================================================
// Module Doctypes (for permission matrix)
// ============================================================================

export interface ModuleDoctypeEntry {
  key: string;
  label: string;
}

export interface ModuleWithDoctypes {
  key: string;
  label: string;
  doctypes: ModuleDoctypeEntry[];
}

// ============================================================================
// Tenant Modules (tenant-level enabled modules)
// ============================================================================

export function useTenantModules() {
  return useApiQuery<ApiSuccessResponse<string[]>>(
    "/platform/modules/my-tenant",
    { staleTime: STALE_TIME.SETTINGS, refetchOnWindowFocus: false }, // settings — rarely changes
  );
}

// ============================================================================
// Module Doctypes (for permission matrix)
// ============================================================================

export function useModuleDoctypes() {
  return useApiQuery<ApiSuccessResponse<ModuleWithDoctypes[]>>(
    "/platform/modules/doctypes",
    { staleTime: STALE_TIME.SETTINGS, refetchOnWindowFocus: false }, // settings — rarely changes
  );
}
